import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import { fileRequestHandlerWithCapacitor } from '../util/localFileReader';
import { getTracksFromSmilDocument } from '../util/smil';
import { SmilItem, EpubJsSpineItem } from '../types/epubjs';
import type { Track } from '../types';
import useReaderEngine from '../engine/use-reader-engine';

type TracksBySmilItem = {
  [smilItemId: string]: Track[];
};

type PendingRequests = {
  [smilItemId: string]: Promise<Track[]>;
};

const useSmilStore = defineStore('book-player-smil', () => {
  const tracksBySmilItem = ref<TracksBySmilItem>({});
  const pendingRequests: PendingRequests = {};

  const { getBookResourceUrl, getIsLocalEpub, book, getElementCFI } = useReaderEngine();

  const loadSmilDocument = (smil: SmilItem): Promise<string> => {
    const url = getBookResourceUrl(smil.path);

    if (book.value.archive?.getText) {
      // If the epubjs book has an archive, we can read the file from there.
      // This only happens when the book was opened from a File or ArrayBuffer,
      // which in turn only happens in the TestPlayer or when previewing books in Giglets.
      return book.value.archive.getText(url);
    } else if (getIsLocalEpub()) {
      return fileRequestHandlerWithCapacitor(url, 'text');
    }
    return axios
      .get(url, {
        // @ts-ignore
        noAuth: true,
      })
      .then((response) => response.data);
  };

  const tracksInPage = async (page: EpubJsSpineItem): Promise<Track[]> => {
    if (page.overlay === undefined) {
      return [];
    }

    const smilItem = page.overlay;

    // Return cached result if available
    if (tracksBySmilItem.value[smilItem.id]) {
      return tracksBySmilItem.value[smilItem.id];
    }

    // If there's already a pending request for this SMIL file, return that promise
    // This prevents duplicate requests when multiple components request the same SMIL file simultaneously
    // @ts-ignore
    if (pendingRequests[smilItem.id]) {
      return pendingRequests[smilItem.id];
    }

    // Create a new promise for this request and cache it
    const requestPromise = (async () => {
      try {
        console.debug(`Loading smil for ${page.id} ${page.path}: ${smilItem.path} `);

        const smilDocument = await loadSmilDocument(smilItem);
        const tracks: Track[] = getTracksFromSmilDocument(smilDocument, smilItem.path, page.path, getBookResourceUrl);

        await Promise.all(
          tracks.map(async (track) => {
            try {
              // Use proper CFI generation that traverses the full DOM structure
              if (track.text.srcFragmentId && track.text.srcFile) {
                track.cfi = await getElementCFI(track.text.srcFile, track.text.srcFragmentId);
              } else {
                track.cfi = page.cfi;
              }
            } catch (error) {
              console.warn(`CFI Resolution failed for track ${track.text.srcFragmentId}`, error);
              // Use the chapter start CFI so it at least highlights something
              track.cfi = page.cfi;
            }
          }),
        );

        // When a book was opened from a File or ArrayBuffer, we make the audio URLs into blobs
        // by loading the data directly from the epubjs archive.
        if (book.value.archive?.getBlob) {
          await Promise.all(
            tracks.map(async (track) => {
              // @ts-ignore
              const canParse = typeof URL.canParse === 'function' ? URL.canParse(track.audio.url) : false;

              if (!canParse) {
                // Normalize the URL
                const url = new URL(`file://${track.audio.url}`).pathname;
                const blob = await book.value.archive!.getBlob!(url);
                if (blob) {
                  track.audio.url = URL.createObjectURL(blob);
                }
              }
            }),
          );
        }

        tracksBySmilItem.value = {
          ...tracksBySmilItem.value,
          [smilItem.id]: tracks,
        };

        return tracks;
      } finally {
        // Clean up the pending request once complete
        delete pendingRequests[smilItem.id];
      }
    })();

    // Store the promise so concurrent requests can reuse it
    pendingRequests[smilItem.id] = requestPromise;

    return requestPromise;
  };

  const $reset = () => {
    tracksBySmilItem.value = {};
    // Clear pending requests on reset
    Object.keys(pendingRequests).forEach((key) => delete pendingRequests[key]);
  };

  const resetState = () => {
    $reset();
  };

  return {
    tracksInPage,
    resetState,
  };
});

export default useSmilStore;
